import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildStartupNavItems, showToast } from '@ideoz/ui';
import { queryClient } from '../../lib/queryClient';
import {
  checkVisitor,
  logout as logoutAction,
  logoutUser,
  removeAccessToken,
  selectUser,
  useStartups,
  useAppDispatch,
  useAppSelector,
  useMyProfilePageFacade,
} from '@ideoz/core';
import { useAuthDialog } from '../../hooks/useAuthDialog';
import { useWebNavigation } from '../../hooks/useWebNavigation';

/**
 * Logic for the user-account profile page: the shared profile facade plus the
 * web-specific logout, auth-dialog adapter, and the startup sidebar (so
 * /profile mirrors the startup shell's nav without highlighting a section).
 */
export function useMyProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const authDialog = useAuthDialog();
  const navigation = useWebNavigation();

  const auth = useMemo(
    () => ({
      isOpen: authDialog.isOpen,
      mode: authDialog.mode,
      isLoading: authDialog.isLoading,
      openLogin: authDialog.openLogin,
      openRegister: authDialog.openRegister,
      close: authDialog.closeDialog,
    }),
    [authDialog]
  );

  // JWT sent via Authorization header; removeAccessToken clears both tokens.
  const handleLogout = useCallback(async () => {
    if (!user) return;
    try {
      await logoutUser();
      removeAccessToken();
      dispatch(logoutAction());
      // Clear the react-query cache too — it's a separate in-memory store the
      // Redux logout reset doesn't touch, so without this the next account
      // sees the previous user's cached startups/attachments until refresh.
      queryClient.clear();
      await checkVisitor(dispatch);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      showToast({ message: t('profile.logoutFailed'), severity: 'error' });
    }
  }, [user, dispatch, navigate, t]);

  const logout = useMemo(() => ({ logout: handleLogout }), [handleLogout]);

  const facade = useMyProfilePageFacade({ navigation, auth, logout, showToast });

  // Match the startup shell's sidebar. /profile isn't nested under a startup,
  // so pick the user's first startup as the target — same items as the shell,
  // just with no active section.
  const { data: startups = [] } = useStartups();

  const startupSlug = startups[0]?.slug ?? '';
  const startupNavItems = useMemo(
    () =>
      buildStartupNavItems({
        t,
        activeSection: '' as any,
        onNavigateToHome: startupSlug ? () => navigate(`/${startupSlug}/home`) : undefined,
        onNavigateToWorkScopes: startupSlug
          ? () => navigate(`/${startupSlug}/work-scopes`)
          : undefined,
        onNavigateToArtifacts: startupSlug
          ? () => navigate(`/${startupSlug}/artifacts`)
          : undefined,
        onNavigateToFiles: startupSlug ? () => navigate(`/${startupSlug}/files`) : undefined,
        onNavigateToDesignSystem: startupSlug
          ? () => navigate(`/${startupSlug}/design-system`)
          : undefined,
        onNavigateToStartupProfile: startupSlug
          ? () => navigate(`/${startupSlug}/profile`)
          : undefined,
        onNavigateToIntegrations: startupSlug
          ? () => navigate(`/${startupSlug}/integrations`)
          : undefined,
      }),
    [startupSlug, navigate, t]
  );

  const facadeWithStartupSidebar = useMemo(
    () => ({
      ...facade,
      sidebar: { ...facade.sidebar, navigationItems: startupNavItems },
    }),
    [facade, startupNavItems]
  );

  return { user, authDialog, facade: facadeWithStartupSidebar };
}
